// API configuration
// For mobile/local network access, detect if we should use local IP

/** Ensure env/API URLs always include a protocol (Netlify vars are sometimes host-only). */
const normalizeApiBaseUrl = (raw) => {
  const trimmed = String(raw || '').trim().replace(/\/+$/, '');
  if (!trimmed) return trimmed;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  // Hostname without protocol — treat as https (Railway public domains).
  return `https://${trimmed}`;
};

const getApiBaseUrl = () => {
  // If explicitly set via environment variable, use that (for production)
  if (process.env.REACT_APP_API_URL) {
    const apiUrl = normalizeApiBaseUrl(process.env.REACT_APP_API_URL);
    console.log('Using configured API URL:', apiUrl);
    return apiUrl;
  }
  
  // Auto-detect based on hostname for deployed environments
  const hostname = window.location.hostname;
  
  // Dev Netlify site (risklo-dev.netlify.app)
  if (hostname.includes('risklo-dev') || hostname === 'risklo-dev.netlify.app') {
    const devUrl = 'https://risklo-development.up.railway.app';
    console.log('Detected dev environment, using:', devUrl);
    return devUrl;
  }

  // Staging detection
  if (hostname.includes('risklostaging') || hostname.includes('staging')) {
    const stagingUrl = 'https://risklo-staging.up.railway.app';
    console.log('Detected staging environment, using:', stagingUrl);
    return stagingUrl;
  }
  
  // Production detection (netlify deployed to custom domain)
  if (hostname.includes('risklo.io') && !hostname.includes('staging') && !hostname.includes('nonprod')) {
    const prodUrl = 'https://risklo-production.up.railway.app';
    console.log('Detected production environment, using:', prodUrl);
    return prodUrl;
  }
  
  // For local development, check if we're on a mobile device or need network access
  
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // Desktop browser - use localhost
    console.log('Using localhost API URL');
    // Note: macOS often uses 5000 (AirTunes). RiskLo backend defaults to 5001 locally.
    return 'http://localhost:5001';
  } else {
    // Mobile device or network access - use the same hostname with port 5001
    // This assumes your computer's IP is accessible from the network
    const apiUrl = `http://${hostname}:5001`;
    console.log('Using network API URL:', apiUrl, '(hostname:', hostname, ')');
    return apiUrl;
  }
};

export const API_BASE_URL = getApiBaseUrl();
console.log('API_BASE_URL initialized to:', API_BASE_URL);

/** NT8 Trade Alert webhook URL — dev uses jean-test feed; production uses nt8/webhook. */
export function getNt8TradeAlertWebhookUrl() {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isDevFeed = hostname.includes('risklo-dev')
    || hostname === 'localhost'
    || hostname === '127.0.0.1';
  const path = isDevFeed ? '/api/webhooks/nt8/jean-test' : '/api/webhooks/nt8/webhook';
  return `${API_BASE_URL}${path}`;
}

export const API_ENDPOINTS = {
  sheets: `${API_BASE_URL}/api/sheets`,
  propFirmPresets: `${API_BASE_URL}/api/prop-firm-presets`,
  adminSheets: `${API_BASE_URL}/api/admin/sheets`,
  adminSheetsCreateStrategy: `${API_BASE_URL}/api/admin/sheets/create-strategy`,
  adminSheetsVisibility: `${API_BASE_URL}/api/admin/sheets/visibility`,
  adminSheetsReorder: `${API_BASE_URL}/api/admin/sheets/reorder`,
  adminSheetByName: (name) => `${API_BASE_URL}/api/admin/sheets/${encodeURIComponent(name)}`,
  adminMasterStrat: `${API_BASE_URL}/api/admin/master-strat`,
  adminEquityStrategies: `${API_BASE_URL}/api/admin/equity-strategies`,
  adminEquityStrategyByName: (name) => `${API_BASE_URL}/api/admin/equity-strategies/${encodeURIComponent(name)}`,
  vectorEquityStrategies: `${API_BASE_URL}/api/vector-equity-strategies`,
  vectorEquityPortfolioConfig: `${API_BASE_URL}/api/vector-equity-portfolio-config`,
  adminEquityPortfolioConfig: `${API_BASE_URL}/api/admin/equity-portfolio-config`,
  adminEquityPortfolioMethod: `${API_BASE_URL}/api/admin/equity-portfolio-method`,
  adminEquityPortfolioLeverageMode: `${API_BASE_URL}/api/admin/equity-portfolio-leverage-mode`,
  adminMonthlyReturnMethods: `${API_BASE_URL}/api/admin/monthly-return-methods`,
  adminBenchmarkSettings: `${API_BASE_URL}/api/admin/benchmark-settings`,
  adminNt8Settings: `${API_BASE_URL}/api/admin/nt8-settings`,
  adminNt8AlertStrategies: `${API_BASE_URL}/api/admin/nt8-alert-strategies`,
  adminNt8WebhookMapping: (params) => `${API_BASE_URL}/api/admin/nt8-webhook-mapping${params ? `?${new URLSearchParams(params)}` : ''}`,
  adminWebhookActivity: (params) => `${API_BASE_URL}/api/admin/webhook-activity${params ? `?${new URLSearchParams(params)}` : ''}`,
  adminWebhookActivityFeed: (params) => `${API_BASE_URL}/api/admin/webhook-activity/feed${params ? `?${new URLSearchParams(params)}` : ''}`,
  emailReportSettings: `${API_BASE_URL}/api/email-reports/settings`,
  emailReportPreview: `${API_BASE_URL}/api/email-reports/preview`,
  emailReportSendTest: `${API_BASE_URL}/api/email-reports/send-test`,
  emailReportAdminSummary: `${API_BASE_URL}/api/email-reports/admin/summary`,
  adminWebhookMappings: `${API_BASE_URL}/api/admin/webhook-mappings`,
  adminWebhookMappingNt8: `${API_BASE_URL}/api/admin/webhook-mappings/nt8`,
  adminWebhookMappingKairos: (slug) => `${API_BASE_URL}/api/admin/webhook-mappings/kairos/${encodeURIComponent(slug)}`,
  adminWebhookMappingTradingview: (slug) => `${API_BASE_URL}/api/admin/webhook-mappings/tradingview/${encodeURIComponent(slug)}`,
  adminWebhookMappingMirrorLive: (slug) => `${API_BASE_URL}/api/admin/webhook-mappings/mirror-live/${encodeURIComponent(slug)}`,
  // Stock-algo feed: observed (symbol, variant) streams + their bindings.
  adminStockAlgoStreams: `${API_BASE_URL}/api/admin/stock-algo/streams`,
  adminStockAlgoBindings: `${API_BASE_URL}/api/admin/stock-algo/bindings`,
  connectorKeysList: `${API_BASE_URL}/api/connector-keys/list`,
  connectorKeysGenerate: `${API_BASE_URL}/api/connector-keys/generate`,
  connectorKeysDelete: (keyId) => `${API_BASE_URL}/api/connector-keys/${encodeURIComponent(keyId)}`,
  vectorBasketDisplay: (futures = [], equityAlgos = []) => {
    const params = new URLSearchParams();
    if (futures.length) params.set('futures', futures.join(','));
    if (equityAlgos.length) params.set('equityAlgos', equityAlgos.join('|'));
    const qs = params.toString();
    return `${API_BASE_URL}/api/vector-basket-display${qs ? `?${qs}` : ''}`;
  },
  adminVectorBasketDisplay: (futures = [], equityAlgos = []) => {
    const params = new URLSearchParams();
    if (futures.length) params.set('futures', futures.join(','));
    if (equityAlgos.length) params.set('equityAlgos', equityAlgos.join('|'));
    const qs = params.toString();
    return `${API_BASE_URL}/api/admin/vector-basket-display${qs ? `?${qs}` : ''}`;
  },
  adminVectorBasketDisplaySave: `${API_BASE_URL}/api/admin/vector-basket-display`,
  vectorScenariosEnabled: `${API_BASE_URL}/api/vector-scenarios/enabled`,
  adminScenarios: `${API_BASE_URL}/api/admin/scenarios`,
  // My brand (Brand Admins): settings, Whop, team.
  myBrand: `${API_BASE_URL}/api/entities/my-brand`,
  myBrandTeam: `${API_BASE_URL}/api/entities/my-brand/team`,
  myBrandTestWhop: `${API_BASE_URL}/api/entities/my-brand/test-whop`,
  // Strategies page (Brand Admins): own brand only.
  adminBrandStrategies: `${API_BASE_URL}/api/admin/strategies`,
  adminBrandStrategyWebhook: (slug) => `${API_BASE_URL}/api/admin/strategies/${encodeURIComponent(slug)}/webhook`,
  adminBrandStrategyFills: (slug) => `${API_BASE_URL}/api/admin/strategies/${encodeURIComponent(slug)}/fills`,
  adminBrandStrategyLiveSince: (slug) => `${API_BASE_URL}/api/admin/strategies/${encodeURIComponent(slug)}/live-since`,
  adminBrandStrategyTradeDisplay: (slug) => `${API_BASE_URL}/api/admin/strategies/${encodeURIComponent(slug)}/trade-display`,
  adminBrandStrategyConnectLive: (slug) => `${API_BASE_URL}/api/admin/strategies/${encodeURIComponent(slug)}/connect-live`,
  adminBrandStrategyVisibility: (slug) => `${API_BASE_URL}/api/admin/strategies/${encodeURIComponent(slug)}/visibility`,
  adminBrandStrategyName: (slug) => `${API_BASE_URL}/api/admin/strategies/${encodeURIComponent(slug)}/name`,
  adminBrandStrategyPreviewCsv: `${API_BASE_URL}/api/admin/strategies/preview-csv`,
  adminScenarioBySlug: (slug) => `${API_BASE_URL}/api/admin/scenarios/${encodeURIComponent(slug)}`,
  adminScenarioRegenerateWebhookToken: (slug) => `${API_BASE_URL}/api/admin/scenarios/${encodeURIComponent(slug)}/regenerate-webhook-token`,
  adminScenarioNt8LiveOverlay: (slug) => `${API_BASE_URL}/api/admin/scenarios/${encodeURIComponent(slug)}/nt8-live-overlay`,
  adminScenarioCatalog: `${API_BASE_URL}/api/admin/scenario-catalog`,
  adminScenarioEquity: (name) => `${API_BASE_URL}/api/admin/scenario-data/equity/${encodeURIComponent(name)}`,
  adminScenarioEquityImport: (name) => `${API_BASE_URL}/api/admin/scenario-data/equity/${encodeURIComponent(name)}/import-csv`,
  adminScenarioEquityExportCsv: (name) => `${API_BASE_URL}/api/admin/scenario-data/equity/${encodeURIComponent(name)}/export-csv`,
  adminScenarioTrades: (name) => `${API_BASE_URL}/api/admin/scenario-data/trades/${encodeURIComponent(name)}`,
  adminScenarioTradesImport: (name) => `${API_BASE_URL}/api/admin/scenario-data/trades/${encodeURIComponent(name)}/import-csv`,
  adminScenarioTradesParseCsv: (name) => `${API_BASE_URL}/api/admin/scenario-data/trades/${encodeURIComponent(name)}/parse-csv`,
  adminScenarioTradesReloadCsv: (name) => `${API_BASE_URL}/api/admin/scenario-data/trades/${encodeURIComponent(name)}/reload-csv`,
  adminScenarioTradesExportCsv: (name) => `${API_BASE_URL}/api/admin/scenario-data/trades/${encodeURIComponent(name)}/export-csv`,
  adminScenarioFutures: (name) => `${API_BASE_URL}/api/admin/scenario-data/futures/${encodeURIComponent(name)}`,
  adminScenarioInstrument: (name) => `${API_BASE_URL}/api/admin/scenario-data/instrument/${encodeURIComponent(name)}`,
  strategyInstruments: `${API_BASE_URL}/api/strategy-instruments`,
  adminScenarioNtConnector: (connectionId) => `${API_BASE_URL}/api/admin/scenario-data/nt-connector/${encodeURIComponent(connectionId)}`,
  adminScenarioNtConnectorRematch: (connectionId) => `${API_BASE_URL}/api/admin/scenario-data/nt-connector/${encodeURIComponent(connectionId)}/rematch`,
  adminTierEmails: `${API_BASE_URL}/api/admin/tier-emails`,
  adminFeatureAccess: `${API_BASE_URL}/api/admin/feature-access`,
  adminFeatureAccessUpdate: (key) => `${API_BASE_URL}/api/admin/feature-access/${encodeURIComponent(key)}`,
  featuresMe: `${API_BASE_URL}/api/features/me`,
  // RiskLo AI (assistant)
  aiStatus: `${API_BASE_URL}/api/ai/status`,
  activityPing: `${API_BASE_URL}/api/activity/ping`,
  shareLinks: `${API_BASE_URL}/api/share-links`,
  shareLinkStrategies: `${API_BASE_URL}/api/share-links/strategies`,
  shareLinkPreview: `${API_BASE_URL}/api/share-links/preview`,
  shareLink: (id) => `${API_BASE_URL}/api/share-links/${encodeURIComponent(id)}`,
  shareLinkLeads: (id) => `${API_BASE_URL}/api/share-links/${encodeURIComponent(id)}/leads`,
  publicShare: (token) => `${API_BASE_URL}/api/public/share/${encodeURIComponent(token)}`,
  publicShareLead: (token) => `${API_BASE_URL}/api/public/share/${encodeURIComponent(token)}/lead`,
  adminSiteMetrics: `${API_BASE_URL}/api/admin/site-metrics`,
  adminSiteMetricsUser: (id) => `${API_BASE_URL}/api/admin/site-metrics/users/${encodeURIComponent(id)}`,
  tradeInsights: `${API_BASE_URL}/api/trade-insights`,
  tradeInsightsConsent: `${API_BASE_URL}/api/trade-insights/consent`,
  tradeInsightsSettings: `${API_BASE_URL}/api/trade-insights/settings`,
  tradeInsightsPropFirm: `${API_BASE_URL}/api/trade-insights/prop-firm`,
  tradeInsightsTags: `${API_BASE_URL}/api/trade-insights/tags`,
  aiChat: `${API_BASE_URL}/api/ai/chat`,
  aiConversations: `${API_BASE_URL}/api/ai/conversations`,
  aiConversation: (id) => `${API_BASE_URL}/api/ai/conversations/${encodeURIComponent(id)}`,
  aiFeedback: (id) => `${API_BASE_URL}/api/ai/messages/${encodeURIComponent(id)}/feedback`,
  aiAdminSettings: `${API_BASE_URL}/api/ai/admin/settings`,
  aiAdminUsage: `${API_BASE_URL}/api/ai/admin/usage`,
  aiProfile: `${API_BASE_URL}/api/ai/profile`,
  aiGeneralFeedback: `${API_BASE_URL}/api/ai/feedback`,
  aiAdminFeedbackResolve: (id) => `${API_BASE_URL}/api/ai/admin/feedback/${encodeURIComponent(id)}/resolve`,
  aiAdminPropFirms: `${API_BASE_URL}/api/ai/admin/prop-firms`,
  aiAdminPropFirmAction: (key, action) => `${API_BASE_URL}/api/ai/admin/prop-firms/${encodeURIComponent(key)}/${action}`,
  adminTierEmailsList: (listKey) => `${API_BASE_URL}/api/admin/tier-emails/${encodeURIComponent(listKey)}`,
  adminTierEmailById: (listKey, id) => `${API_BASE_URL}/api/admin/tier-emails/${encodeURIComponent(listKey)}/${id}`,
  adminTierEmailImportEnv: (listKey) => `${API_BASE_URL}/api/admin/tier-emails/${encodeURIComponent(listKey)}/import-env`,
  adminTierEmailExcludeEnv: (listKey) => `${API_BASE_URL}/api/admin/tier-emails/${encodeURIComponent(listKey)}/exclude-env`,
  adminTierEmailRestoreEnv: (listKey) => `${API_BASE_URL}/api/admin/tier-emails/${encodeURIComponent(listKey)}/restore-env`,
  adminTierEmailBrandAccess: (listKey) => `${API_BASE_URL}/api/admin/tier-emails/${encodeURIComponent(listKey)}/brand-access`,
  adminTierEmailMoveAll: (listKey) => `${API_BASE_URL}/api/admin/tier-emails/${encodeURIComponent(listKey)}/move-all`,
  analyze: `${API_BASE_URL}/api/analyze`,
  publicEmbedStrategies: ({ names, entity } = {}) => {
    const params = new URLSearchParams({ names: names || '' });
    if (entity) params.set('entity', entity);
    return `${API_BASE_URL}/api/public/embed/strategies?${params.toString()}`;
  },
  publicEmbedBenchmarkSeries: ({ benchmark, startDate, baseValue } = {}) => {
    const params = new URLSearchParams({ benchmark: benchmark || 'SPY' });
    if (startDate) params.set('startDate', startDate);
    if (baseValue) params.set('baseValue', String(baseValue));
    return `${API_BASE_URL}/api/public/embed/benchmark-series?${params.toString()}`;
  },
  benchmarkSeries: ({ benchmark = 'sp500', startDate, endDate, baseValue } = {}) => {
    const params = new URLSearchParams();
    params.set('benchmark', benchmark);
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    if (baseValue) params.set('baseValue', String(baseValue));
    return `${API_BASE_URL}/api/benchmark-series?${params.toString()}`;
  },
  marketQuotes: ({ symbols } = {}) => {
    const params = new URLSearchParams();
    if (symbols && symbols.length) {
      params.set('symbols', Array.isArray(symbols) ? symbols.join(',') : String(symbols));
    }
    return `${API_BASE_URL}/api/market-data/quotes${params.toString() ? `?${params.toString()}` : ''}`;
  },
  stockQuotes: ({ symbols } = {}) => {
    const params = new URLSearchParams();
    if (symbols && symbols.length) {
      params.set('symbols', Array.isArray(symbols) ? symbols.join(',') : String(symbols));
    }
    return `${API_BASE_URL}/api/market-data/stock-quotes${params.toString() ? `?${params.toString()}` : ''}`;
  },
  supportedMarkets: `${API_BASE_URL}/api/market-data/supported`,
  health: `${API_BASE_URL}/api/health`,
  authRegister: `${API_BASE_URL}/api/auth/register`,
  authLogin: `${API_BASE_URL}/api/auth/login`,
  authSocial: `${API_BASE_URL}/api/auth/social`,
  authMe: `${API_BASE_URL}/api/auth/me`,
  authLogout: `${API_BASE_URL}/api/auth/logout`,
  authVerifyEmail: (token) => `${API_BASE_URL}/api/auth/verify-email?token=${encodeURIComponent(token)}`,
  authResendVerification: `${API_BASE_URL}/api/auth/resend-verification`,
  authForgotPassword: `${API_BASE_URL}/api/auth/forgot-password`,
  authResetPassword: `${API_BASE_URL}/api/auth/reset-password`,
  authDefaults: `${API_BASE_URL}/api/auth/defaults`,
  authDisclaimer: `${API_BASE_URL}/api/auth/disclaimer`,
  authDisclaimerAccept: `${API_BASE_URL}/api/auth/disclaimer/accept`,
  authMicrosoftStart: (frontendUrl) =>
    `${API_BASE_URL}/api/auth/microsoft/start?frontendUrl=${encodeURIComponent(frontendUrl)}`,
  authWhopStart: (frontendUrl) =>
    `${API_BASE_URL}/api/auth/whop/start?frontendUrl=${encodeURIComponent(frontendUrl)}`,
  authGoogleStart: (frontendUrl) =>
    `${API_BASE_URL}/api/google-sheets/oauth/start?includeSignIn=true&frontendUrl=${encodeURIComponent(frontendUrl)}`,
  proStatus: (email, entitySlug) => {
    const base = `${API_BASE_URL}/api/auth/pro-status?email=${encodeURIComponent(email)}`;
    const slug = entitySlug ? String(entitySlug).trim().toLowerCase() : '';
    return slug ? `${base}&entity=${encodeURIComponent(slug)}` : base;
  },
  maxStatus: (email, entitySlug) => {
    const base = `${API_BASE_URL}/api/auth/max-status?email=${encodeURIComponent(email)}`;
    const slug = entitySlug ? String(entitySlug).trim().toLowerCase() : '';
    return slug ? `${base}&entity=${encodeURIComponent(slug)}` : base;
  },
  subscriptionCreateCheckout: `${API_BASE_URL}/api/subscription/create-checkout`,
  subscriptionManage: `${API_BASE_URL}/api/subscription/manage`,
  sendRiskSummary: `${API_BASE_URL}/api/send-risk-summary`,
  currentResults: (email) => `${API_BASE_URL}/api/current-results${email ? `?email=${encodeURIComponent(email)}` : ''}`,
  strategySheet: (name, email) =>
    `${API_BASE_URL}/api/strategy-sheet?name=${encodeURIComponent(name)}${
      email ? `&email=${encodeURIComponent(email)}` : ''
    }`,
  /** Read-only mirror of the Vector Results Google Sheet, all visible tabs. */
  resultsSpreadsheetSheets: `${API_BASE_URL}/api/results-spreadsheet/sheets`,
  resultsSpreadsheetData: (name) =>
    `${API_BASE_URL}/api/results-spreadsheet/data?name=${encodeURIComponent(name)}`,
  /** Max-only: Vector add-on pipeline (no Google Sheets) */
  vectorNtCurrentResults: (email) =>
    `${API_BASE_URL}/api/vector-nt/current-results${email ? `?email=${encodeURIComponent(email)}` : ''}`,
  vectorNtStrategySheet: (name, email) =>
    `${API_BASE_URL}/api/vector-nt/strategy-sheet?name=${encodeURIComponent(name)}${
      email ? `&email=${encodeURIComponent(email)}` : ''
    }`,
  vectorNtSheets: (email) =>
    `${API_BASE_URL}/api/vector-nt/sheets${email ? `?email=${encodeURIComponent(email)}` : ''}`,
  vectorNtPortfolioAnalytics: (email) =>
    `${API_BASE_URL}/api/vector-nt/portfolio-analytics${email ? `?email=${encodeURIComponent(email)}` : ''}`,
  vectorNtExport: (sheet, email) => {
    const params = new URLSearchParams();
    if (email) params.set('email', email);
    if (sheet) params.set('sheet', sheet);
    return `${API_BASE_URL}/api/vector-nt/export?${params.toString()}`;
  },
  feedback: `${API_BASE_URL}/api/feedback`,
  dashboards: `${API_BASE_URL}/api/dashboards`,
  dashboardById: (id) => `${API_BASE_URL}/api/dashboards/${encodeURIComponent(id)}`,
  dashboardDefaultTemplate: `${API_BASE_URL}/api/dashboards/default-template`,
  customMultistrats: `${API_BASE_URL}/api/custom-multistrats`,
  customMultistratById: (id) => `${API_BASE_URL}/api/custom-multistrats/${encodeURIComponent(id)}`,
  customMultistratData: (id) => `${API_BASE_URL}/api/custom-multistrats/${encodeURIComponent(id)}/data`,
  hedgingRiskAnalysis: `${API_BASE_URL}/api/hedging-risk/analysis`,
  propFirmPlannerRank: `${API_BASE_URL}/api/prop-firm-planner/rank`,
  propFirmPlannerReplay: `${API_BASE_URL}/api/prop-firm-planner/replay`,
  adminStatus: `${API_BASE_URL}/api/admin/status`,
  adminFeedback: (params) => `${API_BASE_URL}/api/admin/feedback${params ? `?${new URLSearchParams(params)}` : ''}`,
  adminFeedbackUpdate: (id) => `${API_BASE_URL}/api/admin/feedback/${id}`,
  adminFeedbackBulk: `${API_BASE_URL}/api/admin/feedback/bulk`,
  adminChangeLogs: (params) => `${API_BASE_URL}/api/admin/change-logs${params ? `?${new URLSearchParams(params)}` : ''}`,
  adminChangeLogById: (id) => `${API_BASE_URL}/api/admin/change-logs/${id}`,
  adminMetricSnapshots: (params) => `${API_BASE_URL}/api/admin/metric-snapshots${params ? `?${new URLSearchParams(params)}` : ''}`,
  adminMetricSnapshotsDiff: (params) => `${API_BASE_URL}/api/admin/metric-snapshots/diff?${new URLSearchParams(params)}`,
  adminMetricSnapshotsRun: `${API_BASE_URL}/api/admin/metric-snapshots/run`,
  adminMetricAlerts: (params) => `${API_BASE_URL}/api/admin/metric-alerts${params ? `?${new URLSearchParams(params)}` : ''}`,
  adminMetricAlertById: (id) => `${API_BASE_URL}/api/admin/metric-alerts/${id}`,
  adminAlerts: (params) => `${API_BASE_URL}/api/admin/alerts${params ? `?${new URLSearchParams(params)}` : ''}`,
  adminAlertsCount: `${API_BASE_URL}/api/admin/alerts/count`,
  tradeWatchAlerts: (params) => `${API_BASE_URL}/api/trade-watch/alerts${params ? `?${new URLSearchParams(params)}` : ''}`,
  tradeWatchAlertsCount: `${API_BASE_URL}/api/trade-watch/alerts/count`,
  tradeWatchAcknowledge: (id) => `${API_BASE_URL}/api/trade-watch/alerts/${encodeURIComponent(id)}/acknowledge`,
  tradeWatchSettings: `${API_BASE_URL}/api/trade-watch/settings`,
  tradeWatcherBoard: `${API_BASE_URL}/api/trade-watcher/board`,
  tradeWatcherConnections: `${API_BASE_URL}/api/trade-watcher/connections`,
  alpacaAlerts: `${API_BASE_URL}/api/alpaca-alerts`,
  alpacaAlertsSettings: `${API_BASE_URL}/api/alpaca-alerts/settings`,
  alpacaAlertsCheck: `${API_BASE_URL}/api/alpaca-alerts/check`,
  tradeWatcherMyHistory: (days = 90) => `${API_BASE_URL}/api/trade-watcher/my-history?days=${encodeURIComponent(days)}`,
  tradeWatcherMyEvents: (limit = 30, date = null) => `${API_BASE_URL}/api/trade-watcher/my-events?limit=${encodeURIComponent(limit)}${date ? `&date=${encodeURIComponent(date)}` : ''}`,
  tradeWatcherLogs: (feedId, ntAccount) => `${API_BASE_URL}/api/trade-watcher/logs?${new URLSearchParams({ feedId: String(feedId), ntAccount })}`,
  tradeWatcherCsa: `${API_BASE_URL}/api/trade-watcher/csa`,
  tradeWatcherCsas: `${API_BASE_URL}/api/trade-watcher/csas`,
  tradeWatcherAutoClose: `${API_BASE_URL}/api/trade-watcher/auto-close`,
  tradeWatcherAutoCloseConsent: `${API_BASE_URL}/api/trade-watcher/auto-close/consent`,
  tradeWatcherRemove: `${API_BASE_URL}/api/trade-watcher/accounts/remove`,
  tradeWatcherRestore: `${API_BASE_URL}/api/trade-watcher/accounts/restore`,
  tradeWatcherRemoved: `${API_BASE_URL}/api/trade-watcher/accounts/removed`,
  tradeWatcherClientLinks: `${API_BASE_URL}/api/trade-watcher/client-links`,
  tradeWatcherFeed: `${API_BASE_URL}/api/trade-watcher/feed`,
  tradeWatcherFeedRegenerate: `${API_BASE_URL}/api/trade-watcher/feed/regenerate`,
  tradeWatcherFeedClear: `${API_BASE_URL}/api/trade-watcher/feed/clear`,
  tradeWatcherSelfUseVisibility: `${API_BASE_URL}/api/trade-watcher/settings/self-use-visibility`,
  tradeWatcherPayloads: `${API_BASE_URL}/api/trade-watcher/payloads?limit=25`,
  tradeWatcherAddonDownload: `${API_BASE_URL}/api/trade-watcher/addon/download`,
  tradeWatcherAddonInfo: `${API_BASE_URL}/api/trade-watcher/addon/info`,
  // RiskLo Tally 2 (Full Admin testing): loader packages, core build kit, releases.
  tradeWatcherAddonV2Download: (file) => `${API_BASE_URL}/api/trade-watcher/addon/v2/download?file=${encodeURIComponent(file)}`,
  tradeWatcherAddonV2Info: `${API_BASE_URL}/api/trade-watcher/addon/v2/info`,
  tallyCoreAdminState: `${API_BASE_URL}/api/tally-core/admin/state`,
  tallyCoreAdminPublish: `${API_BASE_URL}/api/tally-core/admin/publish`,
  tallyCoreAdminPromote: `${API_BASE_URL}/api/tally-core/admin/promote`,
  tallyCoreAdminCanary: `${API_BASE_URL}/api/tally-core/admin/canary`,
  tradeWatcherClientFeed: `${API_BASE_URL}/api/trade-watcher/client-feed`,
  // NinjaTrader usually runs on another machine, so local dev can point the feed
  // at a tunnel (e.g. cloudflared) via REACT_APP_TRADE_WATCHER_INGEST_BASE.
  tradeWatcherIngestUrl: (token) => `${(process.env.REACT_APP_TRADE_WATCHER_INGEST_BASE || API_BASE_URL).replace(/\/+$/, '')}/api/trade-watcher/ingest/${token}`,
  adminAlertsRunReconciliation: `${API_BASE_URL}/api/admin/alerts/run-reconciliation`,
  adminAlertRefillMissingDays: (id) => `${API_BASE_URL}/api/admin/alerts/${encodeURIComponent(id)}/refill-missing-days`,
  adminAlertById: (id) => `${API_BASE_URL}/api/admin/alerts/${id}`,
  adminStrategyMetadataValidate: `${API_BASE_URL}/api/admin/strategy-metadata/validate`,
  adminBetaInvite: `${API_BASE_URL}/api/admin/beta-invite`,
  adminStrategySubmissions: (params) => `${API_BASE_URL}/api/admin/strategy-submissions${params ? `?${new URLSearchParams(params)}` : ''}`,
  adminStrategySubmission: (id) => `${API_BASE_URL}/api/admin/strategy-submissions/${encodeURIComponent(id)}`,
  adminStrategySubmissionWithdraw: (id) => `${API_BASE_URL}/api/admin/strategy-submissions/${encodeURIComponent(id)}/withdraw`,
  adminStrategySubmissionApprove: (id) => `${API_BASE_URL}/api/admin/strategy-submissions/${encodeURIComponent(id)}/approve`,
  adminStrategySubmissionReject: (id) => `${API_BASE_URL}/api/admin/strategy-submissions/${encodeURIComponent(id)}/reject`,
  adminWhopRiskloMetrics: `${API_BASE_URL}/api/admin/whop-risklo-metrics`,
  adminDeleteStressTestAccounts: `${API_BASE_URL}/api/ninjatrader/admin/delete-stress-test-accounts`,
  economicCalendarHighImpact: (month, impacts) => {
    const params = new URLSearchParams();
    params.set('month', month);
    if (impacts && impacts.length) {
      const impactList = Array.isArray(impacts) ? impacts.join(',') : String(impacts);
      params.set('impacts', impactList);
    }
    return `${API_BASE_URL}/api/economic-calendar/high-impact?${params.toString()}`;
  },
  ntConnectorDownload: `${API_BASE_URL}/api/ninjatrader/connector/download`,
  ntConnectorStatus: `${API_BASE_URL}/api/vector-dashboard/nt-connector/status`,
  ntConnectorAccounts: `${API_BASE_URL}/api/vector-dashboard/nt-connector/accounts`,
  ntConnectorPerformance: ({ accounts, startDate, endDate } = {}) => {
    const params = new URLSearchParams();
    if (accounts && accounts.length) params.set('accounts', accounts.join(','));
    if (startDate) params.set('start', startDate);
    if (endDate) params.set('end', endDate);
    return `${API_BASE_URL}/api/vector-dashboard/nt-connector/performance?${params.toString()}`;
  },
  ntConnectorTrades: ({ accounts, startDate, endDate } = {}) => {
    const params = new URLSearchParams();
    if (accounts && accounts.length) params.set('accounts', accounts.join(','));
    if (startDate) params.set('start', startDate);
    if (endDate) params.set('end', endDate);
    return `${API_BASE_URL}/api/vector-dashboard/nt-connector/trades?${params.toString()}`;
  },
};

